import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Menu, X, ChevronDown, Sparkles } from "lucide-react";
import { COMPANY_INFO, SERVICES } from "../data";

export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
      if (window.innerWidth >= 1200) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const isServiceActive = SERVICES.some(s => location.pathname === `/ydelser/${s.slug}`) || location.pathname === "/ydelser";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Top Utility Bar (Only visible on desktop and when not scrolled) */}
      <div
        className={`bg-slate-900 text-white text-xs transition-all duration-300 overflow-hidden ${
          isScrolled ? "h-0 opacity-0" : "h-10 opacity-100 border-b border-slate-800"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center justify-between">
          <div className="hidden sm:flex items-center gap-2 text-slate-300 font-medium">
            <MapPin className="h-3.5 w-3.5 text-sky-400" />
            <span>Professionel service på hele Fyn</span>
          </div>
          <div className="flex items-center gap-6">
            {COMPANY_INFO.email && (
              <a
                href={`mailto:${COMPANY_INFO.email}`}
                className="flex items-center gap-1.5 text-slate-300 hover:text-sky-400 transition-colors font-medium"
              >
                <Mail className="h-3.5 w-3.5 text-sky-400" />
                <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
              </a>
            )}
            {COMPANY_INFO.phone && (
              <a
                href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                className="flex items-center gap-1.5 text-slate-300 hover:text-sky-400 transition-colors font-semibold"
              >
                <Phone className="h-3.5 w-3.5 text-sky-400" />
                <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div
        className={`w-full transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-md shadow-lg py-3 border-b border-slate-100"
            : "bg-white py-5 border-b border-slate-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/500ed420-e032-4255-bacf-bac59ef7cf9a.png"
                alt="Skræp Vinduespuds"
                className="h-10 sm:h-12 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation (>= 1200px) */}
            {!isMobile && (
              <nav className="flex items-center gap-8">
                <NavLink
                  to="/"
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-wider transition-colors hover:text-sky-500 py-2 relative ${
                      isActive ? "text-sky-500" : "text-slate-800"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      Forside
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
                      )}
                    </>
                  )}
                </NavLink>

                {/* Ydelser Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    onMouseEnter={() => setIsDropdownOpen(true)}
                    className={`flex items-center gap-1 text-sm font-bold uppercase tracking-wider transition-colors hover:text-sky-500 py-2 relative ${
                      isServiceActive ? "text-sky-500" : "text-slate-800"
                    }`}
                  >
                    Ydelser
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
                    {isServiceActive && (
                      <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
                    )}
                  </button>

                  {isDropdownOpen && (
                    <div
                      className="absolute left-0 mt-2 w-64 bg-white rounded-md shadow-xl border border-slate-100 py-2 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                      onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                      <Link
                        to="/ydelser"
                        className="block px-4 py-2.5 text-xs font-extrabold uppercase tracking-wider text-slate-900 hover:bg-slate-50 hover:text-sky-500 transition-colors border-b border-slate-50"
                      >
                        Se alle ydelser
                      </Link>
                      {SERVICES.map((service) => (
                        <Link
                          key={service.id}
                          to={`/ydelser/${service.slug}`}
                          className="block px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-sky-500 transition-colors"
                        >
                          {service.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <NavLink
                  to="/galleri"
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-wider transition-colors hover:text-sky-500 py-2 relative ${
                      isActive ? "text-sky-500" : "text-slate-800"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      Galleri
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
                      )}
                    </>
                  )}
                </NavLink>

                <NavLink
                  to="/om-os"
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-wider transition-colors hover:text-sky-500 py-2 relative ${
                      isActive ? "text-sky-500" : "text-slate-800"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      Om Skræp
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
                      )}
                    </>
                  )}
                </NavLink>

                <NavLink
                  to="/kontakt"
                  className={({ isActive }) =>
                    `text-sm font-bold uppercase tracking-wider transition-colors hover:text-sky-500 py-2 relative ${
                      isActive ? "text-sky-500" : "text-slate-800"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      Kontakt
                      {isActive && (
                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-sky-500" />
                      )}
                    </>
                  )}
                </NavLink>
              </nav>
            )}

            {/* Action Buttons (Desktop >= 1200px) */}
            {!isMobile && (
              <div className="flex items-center gap-6">
                {COMPANY_INFO.phone && (
                  <a
                    href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                    className="flex items-center gap-2 text-slate-800 hover:text-sky-500 font-bold text-sm transition-colors whitespace-nowrap"
                  >
                    <div className="bg-sky-50 p-2 rounded-md text-sky-500">
                      <Phone className="h-4 w-4" />
                    </div>
                    <span className="inline-block">{COMPANY_INFO.phone}</span>
                  </a>
                )}
                <Link
                  to="/kontakt"
                  className="bg-sky-500 hover:bg-sky-600 text-white px-6 py-3 rounded-md text-sm font-bold uppercase tracking-wider transition-all shadow-sm"
                >
                  Få et tilbud
                </Link>
              </div>
            )}

            {/* Mobile Menu Button (< 1200px) */}
            {isMobile && (
              <div className="flex items-center gap-3">
                {COMPANY_INFO.phone && (
                  <a
                    href={`tel:${COMPANY_INFO.phone.replace(/\s+/g, "")}`}
                    className="bg-sky-50 text-sky-500 p-2.5 rounded-md hover:bg-sky-100 transition-colors"
                    aria-label="Ring os op"
                  >
                    <Phone className="h-5 w-5" />
                  </a>
                )}
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="text-slate-800 hover:text-sky-500 p-2 rounded-md border border-slate-100 hover:bg-slate-50 transition-colors"
                  aria-label="Menu"
                >
                  {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu (< 1200px) */}
      {isMobile && isOpen && (
        <div className="bg-white border-t border-slate-100 py-4 px-4 shadow-inner animate-in fade-in slide-in-from-top-5 duration-200 max-h-[calc(100vh-80px)] overflow-y-auto">
          <div className="flex flex-col gap-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-4 py-3 rounded-md text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-sky-50 text-sky-500" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              Forside
            </NavLink>

            {/* Mobile Ydelser Links */}
            <div className="border-b border-slate-50 pb-2 mb-2">
              <div className="px-4 py-2 text-xs font-extrabold text-slate-400 uppercase tracking-wider">
                Ydelser
              </div>
              <Link
                to="/ydelser"
                className="block px-6 py-2.5 text-sm font-bold text-sky-500 hover:bg-sky-50 rounded-md"
              >
                Se alle ydelser
              </Link>
              {SERVICES.map((service) => (
                <Link
                  key={service.id}
                  to={`/ydelser/${service.slug}`}
                  className="block px-6 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-md transition-colors"
                >
                  {service.title}
                </Link>
              ))}
            </div>

            <NavLink
              to="/galleri"
              className={({ isActive }) =>
                `px-4 py-3 rounded-md text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-sky-50 text-sky-500" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              Galleri
            </NavLink>

            <NavLink
              to="/om-os"
              className={({ isActive }) =>
                `px-4 py-3 rounded-md text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-sky-50 text-sky-500" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              Om Skræp
            </NavLink>

            <NavLink
              to="/kontakt"
              className={({ isActive }) =>
                `px-4 py-3 rounded-md text-base font-bold uppercase tracking-wider transition-colors ${
                  isActive ? "bg-sky-50 text-sky-500" : "text-slate-700 hover:bg-slate-50"
                }`
              }
            >
              Kontakt
            </NavLink>

            <div className="pt-4 border-t border-slate-100 mt-2">
              <Link
                to="/kontakt"
                className="block w-full text-center bg-sky-500 hover:bg-sky-600 text-white py-3 rounded-md font-bold uppercase tracking-wider transition-colors shadow-sm"
              >
                Få et uforpligtende tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
